import { Controller } from "@hotwired/stimulus";
import type TomSelect from "tom-select";

export default class extends Controller {
  static targets = ["source", "capability", "level", "references", "picker"];
  static values = { options: Object };

  declare readonly sourceTarget: HTMLSelectElement | HTMLInputElement;
  declare readonly capabilityTarget: HTMLElement;
  declare readonly levelTarget: HTMLSelectElement;
  declare readonly referencesTarget: HTMLFieldSetElement;
  declare readonly pickerTarget: HTMLSelectElement & { tomselect?: TomSelect };
  declare readonly optionsValue: Record<string, [string, string][]>;

  private currentSource = "";
  private selections = new Map<string, string[]>();

  connect() {
    this.currentSource = this.sourceTarget.value;
    this.change();
  }

  change() {
    const source = this.sourceTarget;
    const selected =
      source instanceof HTMLSelectElement
        ? source.options[source.selectedIndex]
        : source;
    const nist = selected?.dataset.nist === "true";
    this.capabilityTarget.hidden = !nist;
    this.levelTarget.disabled = !nist;
    if (!nist) this.levelTarget.value = "";
    const enabled = Object.hasOwn(this.optionsValue, source.value);
    this.referencesTarget.hidden = !enabled;
    this.referencesTarget.disabled = !enabled;
    this.pickerTarget.disabled = !enabled;
    const picker = this.pickerTarget.tomselect;
    if (enabled) picker?.enable();
    else picker?.disable();
    if (source.value !== this.currentSource)
      this.changeReferences(source.value);
  }

  private changeReferences(source: string) {
    const select = this.pickerTarget;
    const picker = select.tomselect;
    this.selections.set(
      this.currentSource,
      picker
        ? [...picker.items]
        : Array.from(select.options)
            .filter((option) => option.selected)
            .map((option) => option.value),
    );
    const values = this.selections.get(source) ?? [];
    const options = this.optionsValue[source] ?? [];
    if (picker) {
      picker.clear(true);
      picker.clearOptions();
      picker.addOptions(options.map(([text, value]) => ({ text, value })));
      picker.setValue(values, true);
      picker.setTextboxValue("");
      picker.refreshOptions(false);
    } else {
      select.replaceChildren(
        ...options.map(([text, value]) => {
          const option = document.createElement("option");
          option.text = text;
          option.value = value;
          option.selected = values.includes(value);
          return option;
        }),
      );
    }
    this.currentSource = source;
  }
}
